// MARK:- OS 2.0 web component for the product detail form (variant selection,
// subscription, quantity, add-to-cart). Wraps the <form> in main-product and
// owns every interactive bit of the PDP buying flow.
//
// File naming: standalone web components in this theme use the `component-`
// prefix in the FILE NAME so they sort together in assets/. Custom element name,
// JS class, and data attributes stay short — `<as-product-form>`, AsProductForm.
//
// Architecture: hand-edited asset, NOT a webpack/gulp build output. Loaded once
// per page from sections/main-product.liquid via:
//
//   <script src="{{ 'component-product-form.js' | asset_url }}" type="module"></script>
//
// Markup contract:
//
//   <as-product-form data-product-id="…">
//     <script type="application/json" data-product-json>{{ product | json }}</script>
//     <form id="product-form" action="/cart/add" method="post">
//       <input type="hidden" name="id" value="…" data-as-variant-id>
//
//       <input type="radio" name="option-1" value="…" data-as-option-input data-as-option-position="1">
//       …
//
//       <input type="radio" name="purchase-type" value="one-time" data-as-purchase-type="one-time">
//       <input type="radio" name="purchase-type" value="subscription" data-as-purchase-type="subscription">
//       <input type="radio" name="subscription-interval" value="…" data-as-interval-input data-as-interval-label="…">
//       <span data-as-interval-selected>4 weeks</span>
//       <input type="hidden" name="selling_plan" value="" data-as-selling-plan>
//       <span data-as-price-one-time>$28</span>
//       <span data-as-price-subscription>$28</span>
//
//       <div data-as-quantity-picker>
//         <button type="button" data-as-qty-decrement>…</button>
//         <input type="number" data-as-qty-input>
//         <button type="button" data-as-qty-increment>…</button>
//       </div>
//
//       <button type="submit" data-as-add-button>
//         <span data-as-add-text>Add to basket</span>
//         <span data-as-add-price>$28</span>
//       </button>
//     </form>
//   </as-product-form>
//
// Behavior:
// - Variant selection: option inputs change → resolve matching variant by option
//   values → update price labels, availability (disable add button), and the
//   hidden id input.
// - Subscription: one-time vs subscription radios switch which price is shown.
//   Interval radios drive the dropdown label and the hidden selling_plan input
//   (resolved against variant.selling_plan_allocations when present, else blank
//   — Shopify Subscriptions / a selling-plan app must be installed for real
//   subscription submits to work).
// - Quantity: decrement/increment buttons step the input. Both pickers (initial +
//   sticky bar) sync via this single component.
// - Add-to-cart: intercept submit, fetch /cart/add.js, dispatch cart:added on
//   success (existing cart drawer listens for that), surface error inline.
// - Customizer-safe: self-init via connectedCallback. Shopify mounts/unmounts
//   the element when the section is added/removed; component re-binds correctly.

// MARK:- Shopify's hard-coded cart-cap strings. Both fire from /cart/add.js when a customer hits the per-line limit (one is "added < requested", the other is "already at max"). Substitute settings.cart_quantity_max_message for either.
const SHOPIFY_LIMIT_RE = /(?:Only (\d+) items? were added to your cart due to availability|The maximum quantity of this item is already in your cart)\./i;

class AsProductForm extends HTMLElement {
  connectedCallback() {
    this.product = this.parseProductJson();
    this.form = this.querySelector('form[data-as-product-form]');
    if (!this.product || !this.form) return;

    this.cacheRefs();
    this.bindEvents();
    this.syncSubscriptionUi();
    this.setupFooterTuck();
    this.setupDockPin();
  }

  disconnectedCallback() {
    if (this.footerObserver) this.footerObserver.disconnect();
    if (this.pinObserver) this.pinObserver.disconnect();
    if (this.onPinResize) window.removeEventListener('resize', this.onPinResize);
    clearTimeout(this.pinResizeTimer);
  }

  // MARK:- Mobile persistent buy-bar: tuck the fixed bar (.is-tucked) only while the footer is in view so it never covers footer content. The class also toggles on desktop but its CSS lives inside component-product-form.css's <=960px block, so desktop stays on legacy theme.js behavior.
  setupFooterTuck() {
    const footer = document.querySelector('footer');
    if (!this.stickyBar || !footer) return;
    this.footerObserver = new IntersectionObserver((entries) => {
      const entry = entries[entries.length - 1];
      this.stickyBar.classList.toggle('is-tucked', entry.isIntersecting);
    });
    this.footerObserver.observe(footer);
  }

  // MARK:- Mobile dock/pin buy-bar: bar starts in flow (position:static <=960px in component-product-form.css); .is-pinned (fixed bottom) toggles on once the bar's slot — the zero-height [data-as-bar-sentinel] before it — has scrolled fully into view, and off when scrolling back above it. rootMargin raises the observed bottom edge by the bar height so IO fires exactly when the docked bar's bottom meets the viewport bottom; observer is rebuilt on resize since that height is baked into rootMargin. Class is inert on desktop (CSS lives in the <=960px block).
  setupDockPin() {
    this.barSentinel = this.querySelector('[data-as-bar-sentinel]');
    if (!this.stickyBar || !this.barSentinel) return;

    const observe = () => {
      if (this.pinObserver) this.pinObserver.disconnect();
      const barHeight = this.stickyBar.offsetHeight;
      this.pinObserver = new IntersectionObserver((entries) => {
        const entry = entries[entries.length - 1];
        const pin = entry.boundingClientRect.top < window.innerHeight - barHeight;
        this.stickyBar.classList.toggle('is-pinned', pin);
      }, { rootMargin: `0px 0px -${barHeight}px 0px` });
      this.pinObserver.observe(this.barSentinel);
    };

    observe();

    this.onPinResize = () => {
      clearTimeout(this.pinResizeTimer);
      this.pinResizeTimer = setTimeout(observe, 150);
    };
    window.addEventListener('resize', this.onPinResize);
  }

  parseProductJson() {
    const node = this.querySelector('script[data-product-json]');
    if (!node) return null;
    try {
      return JSON.parse(node.textContent);
    } catch (err) {
      console.error('[as-product-form] Could not parse product JSON', err);
      return null;
    }
  }

  cacheRefs() {
    this.variantIdInput = this.querySelector('[data-as-variant-id]');
    this.sellingPlanInput = this.querySelector('[data-as-selling-plan]');
    this.optionInputs = Array.from(this.querySelectorAll('[data-as-option-input]'));
    this.purchaseRadios = Array.from(this.querySelectorAll('[data-as-purchase-type]'));
    this.intervalInputs = Array.from(this.querySelectorAll('[data-as-interval-input]'));
    this.intervalSelectedLabel = this.querySelector('[data-as-interval-selected]');
    this.priceOneTime = this.querySelector('[data-as-price-one-time]');
    this.priceSubscription = this.querySelector('[data-as-price-subscription]');
    // MARK:- Compare-at strike-through siblings (toggled via [hidden] on variant change)
    this.compareAtOneTime = this.querySelector('[data-as-compare-at-one-time]');
    this.compareAtSubscription = this.querySelector('[data-as-compare-at-subscription]');
    this.compareAtAdd = this.querySelector('[data-as-compare-at-add]');
    this.stickyBar = this.querySelector('[data-as-sticky-bar]');
    this.addButton = this.querySelector('[data-as-add-button]');
    this.addText = this.querySelector('[data-as-add-text]');
    this.addPrice = this.querySelector('[data-as-add-price]');
    this.qtyInputs = Array.from(this.querySelectorAll('[data-as-qty-input]'));
    this.qtyDecrements = Array.from(this.querySelectorAll('[data-as-qty-decrement]'));
    this.qtyIncrements = Array.from(this.querySelectorAll('[data-as-qty-increment]'));
    this.qtyLiveRegions = Array.from(this.querySelectorAll('[data-as-qty-live]'));
    // MARK:- Visible "Limit X per order" indicator next to the QUANTITY label. Hidden in the snippet by default; shown when atMax (refreshQtyButtonState) or after a SHOPIFY_LIMIT_RE-matching 422 (showError). Resolved text lives on data-as-qty-limit-message.
    this.qtyLimitTexts = Array.from(this.querySelectorAll('[data-as-qty-limit-text]'));
    // MARK:- Quantity limits — per-product `data-as-qty-max` on <as-product-form> wins over the body-level theme cap. Per-product value comes from product.metafields.product.quantity_limit (set in main-product.liquid). Empty/missing => fall back to body, which carries settings.cart_quantity_max. Infinity = no theme cap; Shopify still enforces 50 server-side.
    this.qtyMin = parseInt(document.body.dataset.asQtyMin, 10) || 1;
    const rawMax = parseInt(this.dataset.asQtyMax || document.body.dataset.asQtyMax, 10);
    this.qtyMax = Number.isFinite(rawMax) && rawMax > 0 ? rawMax : Infinity;
    this.qtyMessage = document.body.dataset.asQtyMessage || '';
    this.multiplyAddPrice = !!(this.addButton && this.addButton.hasAttribute('data-as-multiply-price'));
    this.currentVariant = this.product.variants.find((v) => String(v.id) === String(this.variantIdInput && this.variantIdInput.value)) || this.product.variants[0];
    this.refreshQtyButtonState();
  }

  bindEvents() {
    this.optionInputs.forEach((input) => {
      input.addEventListener('change', () => this.handleOptionChange());
    });

    this.purchaseRadios.forEach((input) => {
      input.addEventListener('change', () => this.syncSubscriptionUi());
    });

    this.intervalInputs.forEach((input) => {
      input.addEventListener('change', () => this.handleIntervalChange());
    });

    this.qtyDecrements.forEach((btn) => btn.addEventListener('click', () => this.adjustQty(-1)));
    this.qtyIncrements.forEach((btn) => btn.addEventListener('click', () => this.adjustQty(1)));
    this.qtyInputs.forEach((input) => input.addEventListener('input', (e) => this.syncQty(e.target)));
    // MARK:- Keyboard parity for the readonly input — ArrowUp / ArrowDown mirror the +/- buttons so screen-reader/keyboard users keep native qty UX.
    this.qtyInputs.forEach((input) => input.addEventListener('keydown', (e) => {
      if (e.key === 'ArrowUp') { e.preventDefault(); this.adjustQty(1); }
      else if (e.key === 'ArrowDown') { e.preventDefault(); this.adjustQty(-1); }
    }));

    this.form.addEventListener('submit', (event) => this.handleSubmit(event));
  }

  handleOptionChange() {
    const selected = this.collectSelectedOptions();
    const variant = this.findVariantByOptions(selected);
    if (!variant) return;
    this.applyVariant(variant);
  }

  collectSelectedOptions() {
    const byPosition = {};
    this.optionInputs.forEach((input) => {
      if (!input.checked) return;
      const pos = parseInt(input.dataset.asOptionPosition, 10);
      byPosition[pos] = input.value;
    });
    return byPosition;
  }

  findVariantByOptions(byPosition) {
    return this.product.variants.find((variant) => {
      return variant.options.every((value, index) => {
        const expected = byPosition[index + 1];
        return expected == null || expected === value;
      });
    });
  }

  applyVariant(variant) {
    this.currentVariant = variant;
    if (this.variantIdInput) this.variantIdInput.value = variant.id;

    const unitPriceFormatted = this.formatMoney(variant.price);
    if (this.priceOneTime) this.priceOneTime.textContent = unitPriceFormatted;
    if (this.priceSubscription) this.priceSubscription.textContent = unitPriceFormatted;
    // MARK:- Compare-at strike-through: only show when merchant set compare > price
    const onSale = !!variant.compare_at_price && variant.compare_at_price > variant.price;
    const compareFormatted = onSale ? this.formatMoney(variant.compare_at_price) : '';
    [this.compareAtOneTime, this.compareAtSubscription].forEach((el) => {
      if (!el) return;
      if (onSale) {
        el.textContent = compareFormatted;
        el.removeAttribute('hidden');
      } else {
        el.setAttribute('hidden', '');
      }
    });
    this.refreshAddPrice();
    this.refreshCompareAtAdd();

    if (this.addButton) {
      this.addButton.disabled = !variant.available;
    }
    if (this.addText) {
      this.addText.textContent = variant.available ? this.addText.dataset.asAvailableText || this.addText.textContent : 'Sold out';
    }

    this.refreshSellingPlanForVariant();
  }

  refreshAddPrice() {
    if (!this.addPrice || !this.currentVariant) return;
    const qty = this.multiplyAddPrice ? this.currentQuantity() : 1;
    this.addPrice.textContent = this.formatMoney(this.currentVariant.price * qty);
  }

  // MARK:- Mirror refreshAddPrice() for the compare-at strike on the buy-button
  refreshCompareAtAdd() {
    if (!this.compareAtAdd || !this.currentVariant) return;
    const variant = this.currentVariant;
    const onSale = !!variant.compare_at_price && variant.compare_at_price > variant.price;
    if (!onSale) {
      this.compareAtAdd.setAttribute('hidden', '');
      return;
    }
    const qty = this.multiplyAddPrice ? this.currentQuantity() : 1;
    this.compareAtAdd.textContent = this.formatMoney(variant.compare_at_price * qty);
    this.compareAtAdd.removeAttribute('hidden');
  }

  currentQuantity() {
    const input = this.qtyInputs[0];
    if (!input) return 1;
    const parsed = parseInt(input.value, 10);
    return Number.isFinite(parsed) && parsed > 0 ? parsed : 1;
  }

  syncSubscriptionUi() {
    const selected = this.purchaseRadios.find((input) => input.checked);
    const isSubscription = selected && selected.dataset.asPurchaseType === 'subscription';
    this.classList.toggle('is-subscription', !!isSubscription);
    this.refreshSellingPlanForVariant();
  }

  handleIntervalChange() {
    const selected = this.intervalInputs.find((input) => input.checked);
    if (this.intervalSelectedLabel && selected) {
      this.intervalSelectedLabel.textContent = selected.dataset.asIntervalLabel || selected.value;
    }
    this.refreshSellingPlanForVariant();
  }

  refreshSellingPlanForVariant() {
    if (!this.sellingPlanInput) return;
    const isSubscription = this.classList.contains('is-subscription');
    if (!isSubscription) {
      this.sellingPlanInput.value = '';
      return;
    }
    const variant = this.currentVariant || this.product.variants.find((v) => String(v.id) === String(this.variantIdInput && this.variantIdInput.value));
    const allocations = (variant && variant.selling_plan_allocations) || [];
    if (allocations.length === 0) {
      // No real Shopify Subscriptions set up; leave blank so the request still posts.
      this.sellingPlanInput.value = '';
      return;
    }
    const intervalIndex = this.intervalInputs.findIndex((input) => input.checked);
    const allocation = allocations[Math.max(intervalIndex, 0)];
    this.sellingPlanInput.value = (allocation && allocation.selling_plan_id) || '';
  }

  adjustQty(delta) {
    this.qtyInputs.forEach((input) => {
      const raw = parseInt(input.value, 10) + delta;
      const next = Math.min(this.qtyMax, Math.max(this.qtyMin, raw));
      input.value = next;
    });
    this.refreshQtyButtonState();
    this.refreshAddPrice();
    this.refreshCompareAtAdd();
  }

  syncQty(source) {
    const raw = parseInt(source.value, 10) || this.qtyMin;
    const next = Math.min(this.qtyMax, Math.max(this.qtyMin, raw));
    source.value = next;
    this.qtyInputs.forEach((input) => {
      if (input !== source) input.value = next;
    });
    this.refreshQtyButtonState();
    this.refreshAddPrice();
    this.refreshCompareAtAdd();
  }

  // MARK:- Toggle native + aria disabled on every +/- button based on current qty vs theme limits, announce the merchant message via the aria-live regions when at max, and toggle the visible "Limit X per order" span next to the label. Called after every adjust/sync and once at end of cacheRefs() for first-paint accuracy.
  refreshQtyButtonState() {
    const value = this.currentQuantity();
    const atMin = value <= this.qtyMin;
    const atMax = value >= this.qtyMax;
    this.qtyDecrements.forEach((btn) => {
      btn.disabled = atMin;
      if (atMin) btn.setAttribute('aria-disabled', 'true');
      else btn.removeAttribute('aria-disabled');
    });
    this.qtyIncrements.forEach((btn) => {
      btn.disabled = atMax;
      if (atMax) btn.setAttribute('aria-disabled', 'true');
      else btn.removeAttribute('aria-disabled');
    });
    const announcement = atMax && this.qtyMessage ? this.qtyMessage : '';
    this.qtyLiveRegions.forEach((el) => { el.textContent = announcement; });
    // MARK:- Visible limit indicator follows atMax. Empty + hidden when below max.
    this.qtyLimitTexts.forEach((el) => {
      if (atMax) {
        const msg = el.getAttribute('data-as-qty-limit-message') || '';
        el.textContent = msg;
        if (msg) el.removeAttribute('hidden');
        else el.setAttribute('hidden', '');
      } else {
        el.textContent = '';
        el.setAttribute('hidden', '');
      }
    });
  }

  async handleSubmit(event) {
    event.preventDefault();
    if (this.addButton) this.addButton.disabled = true;
    // MARK:- Request the cart-drawer section render in the same round trip so
    // component-cart.js can apply it without a follow-up /cart/update.js fetch.
    const formData = new FormData(this.form);
    formData.append('sections', 'cart-drawer');
    try {
      const response = await fetch('/cart/add.js', {
        method: 'POST',
        headers: { 'Accept': 'application/javascript', 'X-Requested-With': 'XMLHttpRequest' },
        body: formData,
      });
      if (!response.ok) {
        const payload = await response.json().catch(() => ({}));
        throw new Error(payload.description || payload.message || 'Could not add to basket.');
      }
      const data = await response.json();
      // MARK:- Successful add clears any stale cart-drawer error from a prior limit-hit attempt, plus re-syncs the visible limit indicator to the current qty (a previous 422 may have forced it visible while the picker is below max).
      this.clearCartDrawerError();
      this.refreshQtyButtonState();
      // Dispatch BEFORE opening: component-cart.js applies the section synchronously
      // so the drawer opens with fresh contents already in place.
      document.dispatchEvent(new CustomEvent('cart:added', { detail: { response: data } }));
      this.openCartDrawer();
    } catch (err) {
      // MARK:- Surface the error on the PDP (label slot for limit errors, form-bottom <p> for everything else) and on the matching cart line (cart:line-error → showLineError populates the per-line indicator; falls back to the drawer-level red surface only when no line matches, e.g. brand-new add hits limit on first try). Eagerly correct the matching cart line so the drawer reflects reality the moment it opens; cart:refresh then confirms.
      this.showError(err.message);
      this.eagerlyCorrectCartLine(err.message);
      const variantId = this.variantIdInput && this.variantIdInput.value;
      if (variantId) {
        document.dispatchEvent(new CustomEvent('cart:line-error', { detail: { variantId, message: err.message } }));
      }
      this.openCartDrawer();
      document.dispatchEvent(new CustomEvent('cart:refresh'));
    } finally {
      if (this.addButton && this.currentVariant !== undefined ? this.currentVariant.available : true) {
        this.addButton.disabled = false;
      }
    }
  }

  openCartDrawer() {
    const cartDrawer = document.querySelector('#cart.drawer');
    if (!cartDrawer || cartDrawer.classList.contains('is-open')) return;
    const trigger = document.querySelector('[data-as-drawer="cart"]:not([data-as-drawer-hover])');
    if (trigger) trigger.click();
  }

  showError(message) {
    // MARK:- Limit-cap errors (SHOPIFY_LIMIT_RE) route to the visible "Limit X per order" span next to the QUANTITY label — that's the design surface. Other errors fall through to the inline form-bottom <p> in red. Skips the form-bottom append when the limit slot exists, so we don't double-up.
    const isLimitError = SHOPIFY_LIMIT_RE.test(message);
    if (isLimitError && this.qtyLimitTexts.length > 0) {
      this.qtyLimitTexts.forEach((el) => {
        const msg = el.getAttribute('data-as-qty-limit-message') || '';
        if (!msg) return;
        el.textContent = msg;
        el.removeAttribute('hidden');
      });
      return;
    }
    const display = (this.qtyMessage && isLimitError) ? this.qtyMessage : message;
    let inline = this.querySelector('[data-as-error]');
    if (!inline) {
      inline = document.createElement('p');
      inline.setAttribute('data-as-error', '');
      inline.setAttribute('role', 'status');
      inline.setAttribute('aria-live', 'polite');
      inline.style.color = 'var(--error, #FF5656)';
      inline.style.marginTop = '8px';
      this.form.appendChild(inline);
    }
    inline.textContent = display;
  }

  // MARK:- Clears any drawer-level error left over from a prior add attempt (e.g. component-cart.js's showLineError fallback wrote to it when no matching line existed). Per-line errors are owned by the cart line indicator and clear themselves via refreshLineQtyButtonState on the next mutation.
  clearCartDrawerError() {
    const target = document.querySelector('[data-as-cart-error]');
    if (!target) return;
    target.textContent = '';
    target.hidden = true;
  }

  // MARK:- Shopify caps the cart server-side when a customer exceeds inventory or the theme max, but its 422 response doesn't ship the new cart state. Compute the post-add qty for the matching line ourselves: "Only N items were added" → currentQty + N (capped at theme max if set); "maximum quantity already in cart" → theme max (when set). Update the qty input + button state immediately so the drawer reflects reality the moment it opens. cart:refresh re-fetches and self-corrects if our derivation is off.
  eagerlyCorrectCartLine(message) {
    const variantId = this.variantIdInput && this.variantIdInput.value;
    if (!variantId) return;
    const line = document.querySelector(`[data-as-cart-line][data-as-cart-variant-id="${variantId}"]`);
    if (!line) return;
    const input = line.querySelector('[data-as-cart-qty-input]');
    if (!input) return;
    const onlyN = message.match(/Only (\d+) items? were added/i);
    const maxAlready = /The maximum quantity of this item is already in your cart/i.test(message);
    let target = null;
    if (onlyN) {
      const current = parseInt(input.value, 10) || 0;
      target = current + parseInt(onlyN[1], 10);
      if (this.qtyMax !== Infinity) target = Math.min(target, this.qtyMax);
    } else if (maxAlready && this.qtyMax !== Infinity) {
      target = this.qtyMax;
    }
    if (target === null || target === parseInt(input.value, 10)) return;
    input.value = target;
    const inc = line.querySelector('[data-as-cart-qty-increment]');
    const dec = line.querySelector('[data-as-cart-qty-decrement]');
    const atMax = this.qtyMax !== Infinity && target >= this.qtyMax;
    const atMin = target <= 1;
    if (inc) {
      inc.disabled = atMax;
      if (atMax) inc.setAttribute('aria-disabled', 'true');
      else inc.removeAttribute('aria-disabled');
    }
    if (dec) {
      dec.disabled = atMin;
      if (atMin) dec.setAttribute('aria-disabled', 'true');
      else dec.removeAttribute('aria-disabled');
    }
  }

  formatMoney(cents) {
    const value = (cents / 100).toFixed(2);
    const trimmed = value.replace(/\.00$/, '');
    return `$${trimmed}`;
  }
}

if (!customElements.get('as-product-form')) {
  customElements.define('as-product-form', AsProductForm);
}
